import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate } from '@angular/router';
import { isPlatformServer } from '@angular/common';

import { Store } from '@ngrx/store';

import { CartStatus } from '@fcom/dapi/interfaces';
import { snapshot } from '@fcom/rx';
import { isPresent } from '@fcom/core/utils';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { LanguageService } from '@fcom/ui-translate';

import { CartActions } from '../store/actions';
import { cartStatusSelector, cartUrl } from '../store/selectors';
import { BookingCartService } from './booking-cart.service';

@Injectable()
export class BookingGuard implements CanActivate {
  private cartService = inject(BookingCartService);
  private store$ = inject<Store<BookingAppState>>(Store);
  private platform = inject(PLATFORM_ID);
  private configService = inject(ConfigService);
  private languageService = inject(LanguageService);
  private readonly cartStatus = this.store$.selectSignal(cartStatusSelector);

  private initialRoute = true;

  canActivate(route: ActivatedRouteSnapshot): boolean {
    if (isPlatformServer(this.platform)) {
      return true;
    }

    if (this.initialRoute) {
      const cartId = route.queryParamMap.get('cartid');
      const hash = route.queryParamMap.get('hash');

      if (isPresent(cartId) && isPresent(hash)) {
        const locale = this.languageService.localeValue;
        const currentCartUrl = `${this.configService.cfg.cartUrl}/${cartId}?hash=${hash}&locale=${locale}`;
        this.store$.dispatch(CartActions.creationSuccess({ cartUrl: currentCartUrl }));
        this.cartService.triggerLoadCart(currentCartUrl);
      } else {
        const currentCartStatus = this.cartStatus();
        const currentCartUrl = snapshot(this.store$.pipe(cartUrl()));
        if ([CartStatus.INITIAL, CartStatus.OK].indexOf(currentCartStatus) === -1 && !!currentCartUrl) {
          this.cartService.triggerLoadCart(currentCartUrl); // This is called rarely from mobile app.
        }
      }
    }

    this.initialRoute = false;
    return true;
  }
}
